import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
    previsualizarEliminacionTramite,
    eliminarTramiteDefinitivo,
    previsualizarEliminacionCliente,
    eliminarClienteDefinitivo,
} from '../../services/administracionTramiteService';

// Borrado definitivo de un trámite o un cliente. Sirve para los dos porque el preview del backend
// tiene la misma forma; lo único que cambia es a qué endpoint le pega.
//
// El botón recién se habilita cuando el operador escribió el motivo Y copió a mano el
// identificador (el nº de trámite o el CUIL). No es burocracia: obliga a mirar QUÉ se está
// borrando, que es el error que este tipo de pantalla suele provocar.

function ModalEliminarDefinitivo({ open, onClose, tipo, identificador, onEliminado }) {
    const [preview, setPreview] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [confirmacion, setConfirmacion] = useState('');
    const [borrando, setBorrando] = useState(false);

    const esTramite = tipo === 'TRAMITE';

    useEffect(() => {
        if (!open || !identificador) return;
        setPreview(null);
        setMotivo('');
        setConfirmacion('');
        setCargando(true);
        const pedir = esTramite
            ? previsualizarEliminacionTramite(identificador)
            : previsualizarEliminacionCliente(identificador);
        pedir
            .then(setPreview)
            .catch((e) => toast.error(e?.response?.data || 'No se pudo calcular qué se eliminaría.'))
            .finally(() => setCargando(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, identificador]);

    const confirmacionOk = !!preview
        && confirmacion.trim() === (preview.confirmacionRequerida || '');
    const puedeBorrar = !!preview && preview.puedeEliminarse && !!motivo.trim()
        && confirmacionOk && !borrando;

    const borrar = async () => {
        if (!puedeBorrar) return;
        setBorrando(true);
        try {
            const payload = { motivo: motivo.trim(), confirmacion: confirmacion.trim() };
            if (esTramite) {
                await eliminarTramiteDefinitivo(identificador, payload);
            } else {
                await eliminarClienteDefinitivo(identificador, payload);
            }
            toast.success(esTramite ? 'Trámite eliminado definitivamente.' : 'Cliente eliminado definitivamente.');
            onClose();
            if (onEliminado) onEliminado();
        } catch (e) {
            toast.error(e?.response?.data || 'No se pudo eliminar.');
        } finally {
            setBorrando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box sx={{ p: 3 }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="fw-bold text-danger mb-0">
                        <i className="bi bi-trash3-fill me-2" />
                        Eliminar definitivamente
                    </h5>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </div>

                {cargando && (
                    <div className="d-flex align-items-center gap-2 text-muted my-3">
                        <CircularProgress size={18} /> Calculando qué se eliminaría…
                    </div>
                )}

                {preview && (
                    <>
                        <p className="mb-3" style={{ fontSize: '0.92rem' }}>{preview.descripcion}</p>

                        {!preview.puedeEliminarse && (
                            <div className="alert alert-warning" style={{ fontSize: '0.88rem' }}>
                                <i className="bi bi-exclamation-triangle-fill me-2" />
                                {preview.motivoBloqueo}
                            </div>
                        )}

                        <div className="border rounded-3 p-3 mb-3" style={{ background: '#fef2f2' }}>
                            <h6 className="fw-bold text-danger mb-2">Se elimina</h6>
                            {(preview.seElimina || []).length === 0 ? (
                                <p className="mb-0 text-muted" style={{ fontSize: '0.87rem' }}>
                                    Solo la ficha: no tiene nada más cargado.
                                </p>
                            ) : (
                                <ul className="mb-0 ps-3" style={{ fontSize: '0.87rem' }}>
                                    {preview.seElimina.map((item, i) => (
                                        <li key={i}><strong>{item.cantidad}</strong> {item.etiqueta}</li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {(preview.advertencias || []).length > 0 && (
                            <div className="alert alert-light border mb-3" style={{ fontSize: '0.86rem' }}>
                                {preview.advertencias.map((a, i) => <div key={i} className="text-muted">• {a}</div>)}
                            </div>
                        )}

                        {preview.puedeEliminarse && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="motivoEliminacion">
                                        Motivo <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        id="motivoEliminacion"
                                        className="form-control"
                                        rows="2"
                                        maxLength={500}
                                        value={motivo}
                                        onChange={(e) => setMotivo(e.target.value)}
                                        placeholder="Ej: cargado por error, duplicado del trámite #123."
                                        disabled={borrando}
                                    />
                                    <small className="text-muted">
                                        El registro de auditoría es lo único que va a quedar de esto.
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="confirmacionEliminacion">
                                        Para confirmar, escribí <code>{preview.confirmacionRequerida}</code>
                                    </label>
                                    <input
                                        id="confirmacionEliminacion"
                                        type="text"
                                        className={`form-control ${confirmacion && !confirmacionOk ? 'is-invalid' : ''}`}
                                        value={confirmacion}
                                        onChange={(e) => setConfirmacion(e.target.value)}
                                        autoComplete="off"
                                        disabled={borrando}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-secondary" onClick={onClose} disabled={borrando}>
                        Cancelar
                    </button>
                    <button className="btn btn-danger" onClick={borrar} disabled={!puedeBorrar}>
                        {borrando ? 'Eliminando…' : 'Eliminar definitivamente'}
                    </button>
                </div>
            </Box>
        </Dialog>
    );
}

export default ModalEliminarDefinitivo;
